import { createConsumer } from '@rails/actioncable';

const PRESENCE_INTERVAL = 20000;
const RECONNECT_INTERVAL = 1000;

class BaseActionCableConnector {
  static isDisconnected = false;

  constructor(
    app,
    pubsubToken,
    websocketHost = '',
    presenceInterval = PRESENCE_INTERVAL
  ) {
    const websocketURL = websocketHost ? `${websocketHost}/cable` : undefined;

    this.consumer = createConsumer(websocketURL);
    this.app = app;
    this.pubsubToken = pubsubToken;
    this.events = {};
    this.reconnectTimer = null;
    this.presenceTimer = null;
    this.isIntentionallyDisconnected = false;
    this.isAValidEvent = () => true;
    this.subscription = this.createSubscription();
    this.triggerPresenceInterval = () => {
      this.presenceTimer = setTimeout(() => {
        this.subscription.updatePresence();
        this.triggerPresenceInterval();
      }, presenceInterval);
    };
    this.triggerPresenceInterval();
  }

  createSubscription() {
    return this.consumer.subscriptions.create(
      {
        channel: 'RoomChannel',
        pubsub_token: this.pubsubToken,
        account_id: this.app.$store.getters.getCurrentAccountId,
        user_id: this.app.$store.getters.getCurrentUserID,
      },
      {
        updatePresence() {
          this.perform('update_presence');
        },
        received: this.onReceived,
        disconnected: () => {
          // A deliberate disconnect() also fires this callback; re-arming the
          // reconnect timer then would poll a dead consumer forever.
          if (this.isIntentionallyDisconnected) {
            return;
          }
          BaseActionCableConnector.isDisconnected = true;
          this.onDisconnected();
          this.initReconnectTimer();
        },
        rejected: () => {
          this.onSubscriptionRejected();
        },
      }
    );
  }

  updatePubsubToken(pubsubToken) {
    if (!pubsubToken || pubsubToken === this.pubsubToken) {
      return;
    }
    this.pubsubToken = pubsubToken;
    this.subscription.unsubscribe();
    this.subscription = this.createSubscription();
  }

  checkConnection() {
    const isConnectionActive = this.consumer.connection.isOpen();
    const isReconnected =
      BaseActionCableConnector.isDisconnected && isConnectionActive;
    if (isReconnected) {
      this.clearReconnectTimer();
      this.onReconnect();
      BaseActionCableConnector.isDisconnected = false;
    } else {
      this.initReconnectTimer();
    }
  }

  clearReconnectTimer = () => {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  };

  initReconnectTimer = () => {
    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      this.checkConnection();
    }, RECONNECT_INTERVAL);
  };

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {};

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {};

  // eslint-disable-next-line class-methods-use-this
  onSubscriptionRejected = () => {};

  disconnect() {
    this.isIntentionallyDisconnected = true;
    this.clearReconnectTimer();
    if (this.presenceTimer) {
      clearTimeout(this.presenceTimer);
      this.presenceTimer = null;
    }
    this.consumer.disconnect();
  }

  onReceived = ({ event, data } = {}) => {
    if (this.isAValidEvent(data, event)) {
      if (this.events[event] && typeof this.events[event] === 'function') {
        this.events[event](data);
      }
    }
  };
}

export default BaseActionCableConnector;
